// Cross-source (federated) widgets: any chart can render the output of a DuckDB combine SQL.
// `alias` is the key the chart reads from each row, so federated rows need no renaming.

export function federatedSpecOf(widget) {
  const spec = widget?.federated_spec;
  if (!spec) return null;
  const subs = (spec.subqueries || []).filter((s) => s && s.dataset_id);
  if (!subs.length || !String(spec.combine_sql || "").trim()) return null;
  return { subqueries: subs, combine_sql: spec.combine_sql, filter_mode: spec.filter_mode || "auto" };
}

// {{where:<filter_id>:<expr>}} / {{value:<filter_id>}} tokens the author put in the combine SQL.
export const FED_TOKEN_RE = /\{\{\s*(where|value)\s*:\s*([A-Za-z0-9_-]+)/gi;

export function fedTokenIds(sql) {
  const out = [];
  for (const m of String(sql || "").matchAll(FED_TOKEN_RE)) out.push(m[2]);
  return [...new Set(out)];
}

// The editor keeps UI-only state (mode / spec_json); the engine wants semantic_query | spec.
export function federatedPayload(spec) {
  const subqueries = (spec?.subqueries || []).map((s) => {
    const base = { alias: s.alias, dataset_id: s.dataset_id };
    if (s.semantic_query || s.spec) return { ...base, semantic_query: s.semantic_query, spec: s.spec };
    if (s.mode === "advanced") {
      let parsed = {};
      try { parsed = JSON.parse(s.spec_json || "{}"); } catch { parsed = {}; }
      return { ...base, spec: parsed };
    }
    return {
      ...base,
      semantic_query: { dimensions: (s.dimensions || []).map((k) => ({ key: k })), measures: s.measures || [] },
    };
  });
  return {
    subqueries,
    combine_sql: spec?.combine_sql || "",
    filter_mode: spec?.filter_mode || "auto",
  };
}

// Combine-SQL output columns -> chart wells: text columns become dimensions, numbers measures.
export function configFromColumns(columns = [], rows = [], type = "bar") {
  const sample = rows[0] || {};
  const dims = [], meas = [];
  columns.forEach((c) => {
    const v = sample[c];
    const numeric = typeof v === "number" || (v !== null && v !== "" && !Number.isNaN(Number(v)) && typeof v !== "boolean" && !isNaN(parseFloat(v)));
    if (numeric) meas.push({ field: c, alias: c, agg: "sum" });
    else dims.push({ field: c, alias: c, date_part: "none" });
  });
  const cut = (type === "pie" || type === "donut") ? 1 : 2;
  return {
    dimensions: type === "kpi" ? [] : dims.slice(0, cut),
    measures: meas.slice(0, type === "kpi" || type === "pie" || type === "donut" ? 1 : 4),
    limit: 1000,
  };
}

// A spec saved by the AI planner carries `semantic_query` / `spec` per subquery; the editor
// works in its own UI shape (mode + picked keys). Convert once when opening the editor.
export function normalizeFedSpec(spec) {
  const subs = (spec?.subqueries || []).map((s) => {
    if (!s || s.mode || s.dimensions || s.measures || s.spec_json) return s;
    if (s.semantic_query) {
      return {
        alias: s.alias, dataset_id: s.dataset_id, mode: "governed",
        dimensions: (s.semantic_query.dimensions || []).map((d) => (typeof d === "string" ? d : d.key || d.field)).filter(Boolean),
        measures: s.semantic_query.measures || [],
      };
    }
    if (s.spec) {
      return { alias: s.alias, dataset_id: s.dataset_id, mode: "advanced", spec_json: JSON.stringify(s.spec, null, 2) };
    }
    return s;
  });
  return { ...(spec || {}), subqueries: subs };
}
